/**
 * API Client implementation
 *
 * A configurable HTTP client with automatic token injection,
 * silent token refresh on 401, and session expiry handling.
 */
import { dispatchSessionExpired } from './events';
export class ApiClient {
    constructor(config) {
        // Single-flight guard: only one refresh at a time across all concurrent requests
        this.refreshInFlight = null;
        this.baseUrl = config.baseUrl;
        this.getToken = config.getToken ?? (() => null);
        this.onUnauthorized = config.onUnauthorized;
        this.tokenRefresh = config.tokenRefresh;
    }
    handleSessionExpiry() {
        if (this.onUnauthorized) {
            this.onUnauthorized();
        }
        dispatchSessionExpired();
    }
    /**
     * Attempt a silent token refresh using the stored refresh token.
     * Returns true if refresh succeeded, false otherwise.
     *
     * Uses single-flight pattern: if a refresh is already in progress,
     * all callers wait for the same promise instead of firing multiple refreshes.
     */
    async attemptTokenRefresh() {
        if (!this.tokenRefresh)
            return false;
        // Single-flight: reuse in-progress refresh
        if (this.refreshInFlight) {
            return this.refreshInFlight;
        }
        this.refreshInFlight = this._doRefresh();
        try {
            return await this.refreshInFlight;
        }
        finally {
            this.refreshInFlight = null;
        }
    }
    async _doRefresh() {
        const { identityApiUrl, getRefreshToken, onTokensRefreshed } = this.tokenRefresh;
        // No stored refresh token (signed in through the SSO cookie): send none,
        // and identity-api uses the httpOnly refresh_token cookie instead.
        const refreshToken = getRefreshToken();
        try {
            const response = await fetch(`${identityApiUrl}/api/v1/auth/refresh`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(refreshToken ? { refresh_token: refreshToken } : {}),
                credentials: 'include',
            });
            if (!response.ok)
                return false;
            const data = await response.json();
            onTokensRefreshed(data.access_token, data.refresh_token);
            return true;
        }
        catch {
            return false;
        }
    }
    async handleResponse(response, retryFn) {
        if (!response.ok) {
            // Handle 401: attempt silent refresh before giving up
            if (response.status === 401) {
                const hadToken = this.getToken() !== null;
                if (hadToken && retryFn) {
                    const refreshed = await this.attemptTokenRefresh();
                    if (refreshed) {
                        // Retry the original request with the new token
                        const retryResponse = await retryFn();
                        if (retryResponse.ok) {
                            if (retryResponse.status === 204)
                                return undefined;
                            return retryResponse.json();
                        }
                        // Retry also failed — fall through to session expiry
                    }
                }
                if (hadToken) {
                    this.handleSessionExpiry();
                    throw new Error('Session expired. Please log in again.');
                }
            }
            const error = await response.json().catch(() => ({ detail: 'An error occurred' }));
            const message = typeof error.detail === 'string'
                ? error.detail
                : error.detail?.[0]?.msg || 'An error occurred';
            throw new Error(message);
        }
        // Handle 204 No Content
        if (response.status === 204) {
            return undefined;
        }
        return response.json();
    }
    getHeaders(options) {
        const token = this.getToken();
        return {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
            ...options?.headers,
        };
    }
    async get(endpoint, options) {
        const doFetch = () => fetch(`${this.baseUrl}${endpoint}`, {
            headers: this.getHeaders(options),
            credentials: 'include',
        });
        const response = await doFetch();
        return this.handleResponse(response, doFetch);
    }
    async post(endpoint, data, options) {
        const doFetch = () => fetch(`${this.baseUrl}${endpoint}`, {
            method: 'POST',
            headers: this.getHeaders(options),
            body: data ? JSON.stringify(data) : undefined,
            credentials: 'include',
        });
        const response = await doFetch();
        return this.handleResponse(response, doFetch);
    }
    async put(endpoint, data, options) {
        const doFetch = () => fetch(`${this.baseUrl}${endpoint}`, {
            method: 'PUT',
            headers: this.getHeaders(options),
            body: data ? JSON.stringify(data) : undefined,
            credentials: 'include',
        });
        const response = await doFetch();
        return this.handleResponse(response, doFetch);
    }
    async patch(endpoint, data, options) {
        const doFetch = () => fetch(`${this.baseUrl}${endpoint}`, {
            method: 'PATCH',
            headers: this.getHeaders(options),
            body: data ? JSON.stringify(data) : undefined,
            credentials: 'include',
        });
        const response = await doFetch();
        return this.handleResponse(response, doFetch);
    }
    async delete(endpoint, options) {
        const doFetch = () => fetch(`${this.baseUrl}${endpoint}`, {
            method: 'DELETE',
            headers: this.getHeaders(options),
            credentials: 'include',
        });
        const response = await doFetch();
        return this.handleResponse(response, doFetch);
    }
    async uploadFile(endpoint, formData, options) {
        const doFetch = () => {
            const token = this.getToken();
            return fetch(`${this.baseUrl}${endpoint}`, {
                method: 'POST',
                headers: {
                    ...(token && { Authorization: `Bearer ${token}` }),
                    ...options?.headers,
                },
                body: formData,
                credentials: 'include',
            });
        };
        const response = await doFetch();
        return this.handleResponse(response, doFetch);
    }
}
/**
 * Factory function to create a new API client instance.
 */
export function createApiClient(config) {
    return new ApiClient(config);
}
//# sourceMappingURL=client.js.map